/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 91 P0-14 — full-screen SOS countdown + active overlay.
// Mounted once near the app root via App.tsx. Reads state from
// SimpleSOSContext and renders nothing while status is "idle".

import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AlertOctagon, Phone, Shield, X } from "lucide-react";
import {
  SIMPLE_SOS_COUNTDOWN_MS,
  useSimpleSOS,
} from "@/contexts/SimpleSOSContext";

export default function SimpleSOSOverlay() {
  const { t } = useTranslation();
  const { status, countdownRemainingMs, activeEvent, cancel, endActive } = useSimpleSOS();

  const secondsLeft = Math.ceil(countdownRemainingMs / 1000);
  // Drains from 0 to 1 as the countdown elapses — the SVG progress
  // ring below uses this for stroke-dashoffset so the user sees the
  // ring empty out alongside the digit.
  const ringFill = 1 - countdownRemainingMs / SIMPLE_SOS_COUNTDOWN_MS;

  return (
    <AnimatePresence>
      {status === "countdown" && (
        <motion.div
          key="countdown"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-destructive text-destructive-foreground px-6"
          role="alertdialog"
          aria-live="assertive"
          aria-label={t("sos.simple.countdownTitle")}
        >
          <p className="text-lg font-semibold tracking-wide uppercase mb-3 opacity-90">
            {t("sos.simple.countdownTitle")}
          </p>
          <CountdownRing seconds={secondsLeft} fill={ringFill} />
          <p className="text-sm mt-4 opacity-90 text-center max-w-xs">
            {t("sos.simple.countdownBody")}
          </p>
          <button
            type="button"
            onClick={cancel}
            className="mt-10 w-full max-w-xs inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl bg-destructive-foreground text-destructive text-base font-semibold active:scale-95 transition-transform"
          >
            <X size={20} />
            {t("sos.simple.cancel")}
          </button>
        </motion.div>
      )}

      {status === "active" && activeEvent && (
        <motion.div
          key="active"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-background text-foreground overflow-y-auto"
        >
          <div
            className="max-w-md mx-auto px-5 pb-8"
            style={{ paddingTop: "calc(env(safe-area-inset-top) + 24px)" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive" />
              </span>
              <h1 className="text-xl font-bold uppercase tracking-wider">
                {t("sos.simple.activeTitle")}
              </h1>
            </div>

            <p className="text-sm text-muted-foreground mb-5">
              {t("sos.simple.activeBody")}
            </p>

            {/* Location summary */}
            <div className="glass-card p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertOctagon size={20} className="text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    {t("sos.simple.locationHeading")}
                  </p>
                  {typeof activeEvent.lat === "number" && typeof activeEvent.lng === "number" ? (
                    <p className="text-sm font-mono">
                      {activeEvent.lat.toFixed(5)}, {activeEvent.lng.toFixed(5)}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t("sos.simple.locationUnknown")}
                    </p>
                  )}
                  <p className="text-[12px] text-muted-foreground mt-1">
                    {t("sos.simple.triggeredAt", {
                      time: new Date(activeEvent.triggeredAt).toLocaleTimeString(),
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Phase 2 placeholder — contacts-notified list. Will
                surface once SafeCircle SMS fan-out lands. */}
            <div className="glass-card p-4 mb-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                {t("sos.simple.contactsHeading")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("sos.simple.contactsPhase2")}
              </p>
            </div>

            {/* Direct dial 911 — always available, safety-critical. */}
            <a
              href="tel:911"
              className="w-full inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl bg-destructive text-destructive-foreground text-base font-semibold mb-3 active:scale-95 transition-transform"
            >
              <Phone size={20} />
              {t("sos.simple.call911")}
            </a>

            {/* End SOS — required by spec. */}
            <button
              type="button"
              onClick={() => void endActive()}
              className="w-full inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl border-2 border-destructive text-destructive text-base font-semibold active:scale-95 transition-transform"
            >
              <Shield size={20} />
              {t("sos.simple.endSos")}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CountdownRing({ seconds, fill }: { seconds: number; fill: number }) {
  const size = 220;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * fill;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="text-destructive-foreground/25"
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-7xl font-bold tabular-nums">{seconds}</span>
      </div>
    </div>
  );
}
